import { apiClient, createApiClient } from "./client";
import type { ApiPathParams, ApiQueryParams, ApiSuccessResponse } from "./types";

// ---------------------------------------------------------------------------
// GET /profiles/github/{login} -- existing GitHub profile fetch
// ---------------------------------------------------------------------------

const GITHUB_PROFILE_PATH = "/api/v1/profiles/github/{login}" as const;

type GithubProfilePathParams = ApiPathParams<typeof GITHUB_PROFILE_PATH, "get">;
export type GithubProfileQuery = ApiQueryParams<typeof GITHUB_PROFILE_PATH, "get">;
export type GithubProfileResponse = ApiSuccessResponse<typeof GITHUB_PROFILE_PATH, "get">;

export interface GetGithubProfileOptions {
  includeReadmes?: boolean;
}

export async function getGithubProfile(
  login: string,
  options: GetGithubProfileOptions = {}
): Promise<GithubProfileResponse> {
  const pathParams: GithubProfilePathParams = { login };
  const query: GithubProfileQuery | undefined =
    options.includeReadmes === undefined
      ? undefined
      : { include_readmes: options.includeReadmes };

  return apiClient.request(GITHUB_PROFILE_PATH, "get", {
    pathParams,
    query
  });
}

// ---------------------------------------------------------------------------
// GET /profiles/{candidate_id} -- canonical profile by numeric ID
// ---------------------------------------------------------------------------

const CANONICAL_PROFILE_PATH = "/api/v1/profiles/{candidate_id}" as const;

type CanonicalProfilePathParams = ApiPathParams<typeof CANONICAL_PROFILE_PATH, "get">;
export type CanonicalProfileResponse = ApiSuccessResponse<typeof CANONICAL_PROFILE_PATH, "get">;

export async function getCanonicalProfile(
  candidateId: number,
  options?: { cookieHeader?: string }
): Promise<CanonicalProfileResponse> {
  const pathParams: CanonicalProfilePathParams = { candidate_id: candidateId };

  // When called from a Server Component, forward the cookie header so the API
  // can attribute the profile_view to the authenticated user.
  if (options?.cookieHeader) {
    const serverClient = createApiClient({
      headers: { Cookie: options.cookieHeader },
    });
    return serverClient.request(CANONICAL_PROFILE_PATH, "get", { pathParams });
  }

  return apiClient.request(CANONICAL_PROFILE_PATH, "get", {
    pathParams
  });
}

// ---------------------------------------------------------------------------
// GET /profiles/lookup/github?username= -- GitHub lookup (DB-first / live)
// ---------------------------------------------------------------------------

const GITHUB_LOOKUP_PATH = "/api/v1/profiles/lookup/github" as const;

export type GithubLookupQuery = ApiQueryParams<typeof GITHUB_LOOKUP_PATH, "get">;
export type GithubLookupResponse = ApiSuccessResponse<typeof GITHUB_LOOKUP_PATH, "get">;

export async function lookupGithub(
  username: string
): Promise<GithubLookupResponse> {
  const query: GithubLookupQuery = { username };

  return apiClient.request(GITHUB_LOOKUP_PATH, "get", {
    query
  });
}

// ---------------------------------------------------------------------------
// GET /profiles/lookup/linkedin?identifier= -- LinkedIn lookup (DB-only)
// ---------------------------------------------------------------------------

export interface LinkedInLookupResponse {
  status: "found_from_db" | "fetched_live" | "not_found" | "rate_limited" | "generic_error";
  candidate_id: number | null;
  linkedin_handle: string | null;
  display_name: string | null;
  message: string | null;
}

const API_BASE =
  typeof window === "undefined"
    ? process.env.ALISTER_API_URL ?? "http://127.0.0.1:8000"
    : "";

export async function lookupLinkedin(
  identifier: string
): Promise<LinkedInLookupResponse> {
  const url = `${API_BASE}/api/v1/profiles/lookup/linkedin?identifier=${encodeURIComponent(identifier)}`;
  const response = await fetch(url, {
    credentials: "include",
  });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// GET /profiles/lookup/name?q= -- Name-based lookup (ILIKE)
// ---------------------------------------------------------------------------

export interface NameLookupResult {
  candidate_id: number;
  display_name: string;
  primary_company: string | null;
  primary_headline: string | null;
}

export interface NameLookupResponse {
  status: "found" | "no_results";
  results: NameLookupResult[];
  query: string;
  message: string | null;
  total_count: number | null;
}

export async function lookupByName(
  query: string,
  limit: number = 20,
  offset: number = 0
): Promise<NameLookupResponse> {
  const params = new URLSearchParams({ q: query, limit: String(limit), offset: String(offset) });
  const url = `${API_BASE}/api/v1/profiles/lookup/name?${params}`;
  const response = await fetch(url, { credentials: "include" });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// GET /profiles/lookup/company?q= -- Company-based lookup (ILIKE)
// ---------------------------------------------------------------------------

export interface CompanyLookupResult {
  candidate_id: number;
  display_name: string;
  primary_company: string | null;
  primary_headline: string | null;
  matched_company: string | null;
  is_current: boolean;
}

export interface CompanyLookupResponse {
  status: "found" | "no_results";
  results: CompanyLookupResult[];
  query: string;
  message: string | null;
  total_count: number | null;
}

export async function lookupByCompany(
  query: string,
  limit: number = 20,
  offset: number = 0
): Promise<CompanyLookupResponse> {
  const params = new URLSearchParams({ q: query, limit: String(limit), offset: String(offset) });
  const url = `${API_BASE}/api/v1/profiles/lookup/company?${params}`;
  const response = await fetch(url, { credentials: "include" });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// GET /profiles/lookup/education?q= -- Education-based lookup (ILIKE)
// ---------------------------------------------------------------------------

export interface EducationLookupResult {
  candidate_id: number;
  display_name: string;
  primary_company: string | null;
  primary_headline: string | null;
  matched_school: string | null;
}

export interface EducationLookupResponse {
  status: "found" | "no_results";
  results: EducationLookupResult[];
  query: string;
  message: string | null;
  total_count: number | null;
}

export async function lookupByEducation(
  query: string,
  limit: number = 20,
  offset: number = 0
): Promise<EducationLookupResponse> {
  const params = new URLSearchParams({ q: query, limit: String(limit), offset: String(offset) });
  const url = `${API_BASE}/api/v1/profiles/lookup/education?${params}`;
  const response = await fetch(url, { credentials: "include" });

  if (response.status === 401) {
    throw new Error("Not authenticated");
  }
  if (response.status === 429) {
    const body = await response.json();
    throw new Error(body.detail ?? "Rate limit exceeded");
  }
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json();
}
